import { useLocation } from "@remix-run/react";
import { useEffect, useMemo, useRef } from "react";
import WvLink from "../atoms/WvLink";

/** Mirrors the loader's page size (`first` in searchProductLoader). */
const PRODUCTS_PER_PAGE = 12;

/** Pages always shown around the current one, besides the first and last. */
const WINDOW = 1;

// Icon-font glyphs for the arrows (U+F2FA, U+F2FB) — invisible in most editors.
const ARROW_LEFT = "";
const ARROW_RIGHT = "";

type Props = {
  totalProducts: number;
  currentPage: number;
};

/**
 * Builds the page strip: first and last page always, the current page with its
 * neighbours, and `null` wherever a run of pages is skipped (rendered as "…").
 */
export const buildPageStrip = (
  pageCount: number,
  currentPage: number
): (number | null)[] => {
  const shown = new Set<number>([1, pageCount]);
  for (let page = currentPage - WINDOW; page <= currentPage + WINDOW; page++) {
    if (page >= 1 && page <= pageCount) {
      shown.add(page);
    }
  }

  const strip: (number | null)[] = [];
  let previous = 0;
  for (const page of [...shown].sort((a, b) => a - b)) {
    if (previous && page - previous > 1) {
      strip.push(null);
    }
    strip.push(page);
    previous = page;
  }

  return strip;
};

const WvProductListPagination = ({ totalProducts, currentPage }: Props) => {
  const location = useLocation();

  const pageCount = Math.ceil(totalProducts / PRODUCTS_PER_PAGE);

  // Page 1 keeps the clean URL so it matches the canonical the loader emits.
  const hrefFor = useMemo(
    () => (page: number) => {
      const search = new URLSearchParams(location.search);
      if (page <= 1) {
        search.delete("page");
      } else {
        search.set("page", String(page));
      }
      const query = search.toString();
      return query ? `${location.pathname}?${query}` : location.pathname;
    },
    [location.pathname, location.search]
  );

  const strip = useMemo(
    () => buildPageStrip(pageCount, currentPage),
    [pageCount, currentPage]
  );

  // Layout only resets scroll when the pathname changes, and a page click changes
  // just the query string — without this the visitor stays at the bottom of a grid
  // whose contents were replaced above them. Starts at the current page so a
  // deep-linked ?page=N doesn't jump on arrival.
  const scrolledPage = useRef(currentPage);
  useEffect(() => {
    if (scrolledPage.current === currentPage) {
      return;
    }
    scrolledPage.current = currentPage;

    // body is the scroll container (custom.css), so window.scrollTo does nothing.
    const grid = document.querySelector(".productlist");
    const gridTop = grid
      ? grid.getBoundingClientRect().top + document.body.scrollTop
      : 0;

    document.body.scrollTop = Math.max(gridTop, 0);
  }, [currentPage]);

  if (pageCount < 2) {
    return null;
  }

  return (
    <div className="productlist__pagination pagination">
      <div className="pagination__nav">
        {currentPage > 1 && (
          <WvLink
            to={hrefFor(currentPage - 1)}
            className="pagination__nav-item arrows"
            title="Vorige pagina"
            rel="prev"
            data-icon-before={ARROW_LEFT}
          />
        )}

        {strip.map((page, index) =>
          page === null ? (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={`gap-${index}`}
              className="pagination__nav-item pagination__nav-item--separator"
            >
              <span>…</span>
            </span>
          ) : page === currentPage ? (
            <span
              key={page}
              className="pagination__nav-item pagination__nav-item--current"
              aria-current="page"
            >
              <span>{page}</span>
            </span>
          ) : (
            <WvLink key={page} to={hrefFor(page)} className="pagination__nav-item">
              <span>{page}</span>
            </WvLink>
          )
        )}

        {currentPage < pageCount && (
          <WvLink
            to={hrefFor(currentPage + 1)}
            className="pagination__nav-item arrows"
            title="Volgende pagina"
            rel="next"
            data-icon-after={ARROW_RIGHT}
          />
        )}
      </div>
    </div>
  );
};

export default WvProductListPagination;
